import React from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Combobox } from "@/components/ui/combobox";
import { SelectOptionItem } from "@/lib/models/ui";
import { useListCustomers } from "@/lib/api/customer";
import { useListMinimalUsers } from "@/lib/api/users";

interface AllocationFormData {
  year: number;
  amount_allocated: string;
  is_discretionary: boolean;
  customer_id: string;
  employee_id: string;
}

interface AllocationFormProps {
  formData: AllocationFormData;
  setFormData: React.Dispatch<React.SetStateAction<AllocationFormData>>;
  isEditing?: boolean;
}

export function AllocationForm({ formData, setFormData, isEditing = false }: AllocationFormProps) {
  const minimalCustomersListResult = useListCustomers({ minimal: true });
  const minimalUserListResult = useListMinimalUsers();
  
  const customers = (minimalCustomersListResult?.data?.data || []).map((c: any): SelectOptionItem<string> => ({
    label: c.long_name,
    value: c.id,
  }));
  
  const users = (minimalUserListResult?.data?.data || []).map((u: any): SelectOptionItem<string> => ({
    label: u.name,
    value: u.id,
  }));

  const handleInputChange = (field: keyof AllocationFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear + 5 - i).map(year => ({
    label: year.toString(),
    value: year.toString(),
  }));

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="year">Year</Label>
        <Combobox
          dwimOnChange
          isModal
          placeholder="Select year"
          options={years}
          value={formData.year.toString()}
          onValueChange={(value) => handleInputChange("year", parseInt(value))}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="amount">Amount Allocated</Label>
        <Input
          id="amount"
          type="text"
          placeholder="0.00"
          value={formData.amount_allocated}
          onChange={(e) => handleInputChange("amount_allocated", e.target.value)}
        />
      </div>

      <div className="flex items-center space-x-2">
        <Checkbox
          id="discretionary"
          checked={formData.is_discretionary}
          onCheckedChange={(checked) => handleInputChange("is_discretionary", checked as boolean)}
        />
        <Label
          htmlFor="discretionary"
          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
        >
          Discretionary Allocation
        </Label>
      </div>

      <div className="space-y-4">
        <div className="text-sm text-muted-foreground">
          Select either a Customer or an Employee (not both)
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="customer">Customer</Label>
          <Combobox
            dwimOnChange
            isModal
            placeholder="Select customer"
            popoverClassName='md:min-w-[300px] lg:min-w-[400px]'
            options={customers}
            value={formData.customer_id || ""}
            onValueChange={(value) => {
              handleInputChange("customer_id", value);
              if (value) {
                handleInputChange("employee_id", "");
              }
            }}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="employee">Employee</Label>
          <Combobox
            dwimOnChange
            isModal
            placeholder="Select employee"
            popoverClassName='md:min-w-[300px] lg:min-w-[400px]'
            options={users}
            value={formData.employee_id || ""}
            onValueChange={(value) => {
              handleInputChange("employee_id", value);
              if (value) {
                handleInputChange("customer_id", "");
              }
            }}
          />
        </div>
      </div>
    </div>
  );
}
